import React, { useRef, useCallback, useState } from 'react';
import { Form } from 'react-bootstrap';

// ─────────────────────────────────────────────────────────────────
// Input numérico reutilizable con:
//  - Formato ES: display con coma decimal `,`, storage/normalizado con `.`.
//  - Punto del teclado numérico (Numpad Decimal) se convierte a `,` visual.
//  - onWheel bloquea cambio de valor por scroll (bug UX reportado).
//  - Navegación entre celdas con flechas/Enter (comportamiento existente).
// ─────────────────────────────────────────────────────────────────
const MedicionInput = ({ value, onChange, placeholder, inputRef, onNavigate, dataAttrs = {} }) => {
  const hasValue = value !== '' && value !== undefined && value !== null;

  // Estado local para preservar el separador trailing (`49,` mientras el usuario
  // aún no escribe el decimal). El padre parsea con `parseFloat` y perdería el
  // `.`; el localStr lo conserva hasta el blur o hasta que se escriba otro dígito.
  const [localStr, setLocalStr] = useState(null);

  const displayValue = React.useMemo(() => {
    if (localStr !== null) return localStr;
    if (value === '' || value === undefined || value === null) return '';
    return String(value).replace('.', ',');
  }, [value, localStr]);

  const handleChange = (rawInput) => {
    // Sanitiza: acepta dígitos, un solo separador decimal (`.` o `,`) y signo `-`.
    let s = String(rawInput).replace(/[^0-9,.\-]/g, '');
    const firstSep = s.search(/[,.]/);
    if (firstSep !== -1) {
      s = s.slice(0, firstSep + 1) + s.slice(firstSep + 1).replace(/[,.]/g, '');
    }
    // localStr = display (con coma); storageForm = normalizado con punto.
    const displayForm = s.replace('.', ',');
    setLocalStr(displayForm);
    const storageForm = s.replace(',', '.');
    if (storageForm === '' || storageForm === '-') {
      onChange('');
      return;
    }
    // Trailing separator: no emitir aún al padre — evita parseFloat que trunca.
    if (storageForm.endsWith('.')) return;
    onChange(storageForm);
  };

  const handleBlur = () => {
    // Al perder focus, sincronizar con el value real del padre.
    setLocalStr(null);
  };

  const handleKeyDown = (e) => {
    // Numpad Decimal (código en teclado numérico ES suele imprimir `.` incluso
    // con layout ES). Lo interceptamos y agregamos `,` visualmente al inicio
    // del handler — pero React input maneja el value real vía onChange, así
    // que solo necesitamos que displayValue muestre `,`. La conversión ya se
    // hace en handleChange sobre lo que escriba el usuario.
    if (e.key === '.' || e.key === ',' || e.code === 'NumpadDecimal') {
      // No preventDefault: dejamos que el char entre; handleChange lo normaliza.
    }

    // Prevenir que flechas arriba/abajo cambien el valor (bug UX)
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault();
    }

    if (onNavigate) {
      if (e.key === 'ArrowUp') {
        onNavigate('up');
      } else if (e.key === 'ArrowDown') {
        onNavigate('down');
      } else if (e.key === 'ArrowLeft') {
        onNavigate('left');
      } else if (e.key === 'ArrowRight' || e.key === 'Enter') {
        e.preventDefault();
        onNavigate('right');
      }
    }
  };

  // Bloquea cambio de valor por scroll wheel (bug UX reportado).
  const handleWheel = (e) => {
    e.currentTarget.blur();
  };

  return (
    <Form.Control
      type="text"
      inputMode="decimal"
      autoComplete="off"
      size="sm"
      value={displayValue}
      onChange={(e) => handleChange(e.target.value)}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      onWheel={handleWheel}
      placeholder={placeholder}
      className={`medicion-input ${hasValue ? 'medicion-input--filled' : ''}`}
      ref={inputRef}
      {...dataAttrs}
    />
  );
};

// Fallback DOM: cuando la navegación con flechas sale por arriba o abajo de la
// tabla actual (patrones o IBC), buscar la celda equivalente en la tabla hermana
// del MISMO acordeón (misma `magKey`). Permite alternar entre "un valor del
// patrón, un valor del equipo" sin usar el mouse.
//   direction = 'up' | 'down'
//   currentTipo = 'patrones' | 'ibc'  → salta al tipo opuesto.
const focusSiblingCell = ({ magKey, currentTipo, direction, punto, medicion, canal }) => {
  const otroTipo = currentTipo === 'patrones' ? 'ibc' : 'patrones';
  // Query base: mismos punto/medicion/canal en el tipo opuesto y misma magnitud.
  const selectorBase = `input.medicion-input[data-mag="${CSS.escape(String(magKey))}"][data-tipo="${otroTipo}"][data-punto="${punto}"][data-medicion="${medicion}"][data-canal="${canal || 'normal'}"]`;
  const candidatos = Array.from(document.querySelectorAll(selectorBase));
  if (candidatos.length === 0) return false;
  // ArrowDown → primer row del otro tipo. ArrowUp → último row.
  const target = direction === 'down' ? candidatos[0] : candidatos[candidatos.length - 1];
  if (target && typeof target.focus === 'function') {
    target.focus();
    if (typeof target.select === 'function') target.select();
    return true;
  }
  return false;
};

// ─────────────────────────────────────────────────────────────────
// Detección de outlier por dispersión (spec review 2026-09-28).
// Compute std dev de un conjunto de N mediciones per (equipo × punto).
// UMBRAL_STD_ABS: dispersión absoluta orientativa (0.1 según sugerencia
// del operativo). Complementamos con relativo (coef. variación) para no
// dar falso-positivo en escalas grandes.
// Retorna { std, count, exceeds, mean } — `exceeds=true` dispara el resaltado.
// ─────────────────────────────────────────────────────────────────
const UMBRAL_STD_ABS = 0.1;
const UMBRAL_STD_REL = 0.02; // 2% del promedio
const _computeDispersion = (arr) => {
  const nums = (arr || [])
    .map((v) => (v === '' || v === null || v === undefined ? NaN : Number(v)))
    .filter((v) => !Number.isNaN(v));
  if (nums.length < 2) return { std: null, count: nums.length, exceeds: false, mean: null };
  const mean = nums.reduce((a, b) => a + b, 0) / nums.length;
  const variance = nums.reduce((acc, v) => acc + Math.pow(v - mean, 2), 0) / nums.length;
  const std = Math.sqrt(variance);
  // Exceso: se pasa si supera el umbral absoluto Y el relativo. El absoluto
  // atrapa mediciones pequeñas (~0.5); el relativo, escalas grandes (~150).
  const exceedeAbs = std > UMBRAL_STD_ABS;
  const exceedeRel = Math.abs(mean) > 0 && (std / Math.abs(mean)) > UMBRAL_STD_REL;
  return { std, count: nums.length, exceeds: exceedeAbs && exceedeRel, mean };
};

// Formatea el número con decimales dinámicos (más cortos si es pequeño).
const _fmt = (n) => {
  if (n === null || n === undefined || Number.isNaN(n)) return '—';
  const abs = Math.abs(n);
  if (abs === 0) return '0';
  if (abs < 0.001) return n.toExponential(2);
  return n.toFixed(abs < 1 ? 4 : abs < 100 ? 3 : 2);
};

// Estilos inline para outlier — usados en <td> de mediciones y en el header.
const OUTLIER_TD_STYLE = { backgroundColor: '#fdecea', boxShadow: 'inset 0 0 0 2px #dc3545' };
const OUTLIER_BADGE_STYLE = {
  fontSize: '0.7rem', fontWeight: 600, color: '#dc3545',
  display: 'inline-block', marginTop: 2
};
const OK_BADGE_STYLE = {
  fontSize: '0.7rem', color: '#6c757d', display: 'inline-block', marginTop: 2
};

// ─────────────────────────────────────────────────────────────────
// Celda de identificación del equipo
// ─────────────────────────────────────────────────────────────────
const CeldaEquipo = ({ lote }) => {
  const equipo = lote.equipoInOt;
  return (
    <td className="mediciones-table__td mediciones-table__td--equipo"
      style={{ maxWidth: '150px' }}
    >
      <div className="mediciones-table__equipo-label">{equipo?.consecutivo || '—'}</div>
      <div 
        className="mediciones-table__equipo-sub"
        style={{ fontSize: '0.75rem', color: '#555', whiteSpace: 'normal', overflow: 'hidden', textOverflow: 'ellipsis' }}
      >{equipo?.equipo?.item?.nombre}</div>
      <div className="mediciones-table__equipo-sub">{equipo?.equipo?.serie ? `S/N: ${equipo.equipo.serie}` : ''}</div>
    </td>
  );
};

// ═════════════════════════════════════════════════════════════════
// LAYOUT TRANSPUESTA
// Una fila por equipo, puntos como columnas
// Cuando: !histeresisEnTodosLosPuntos Y NO (cantPuntos>4 && cantMed>4)
// ═════════════════════════════════════════════════════════════════
const LayoutTranspuesta = ({ magnitud, lotesCalibracion, datosIBC, datosPuntosCalibracion, datoHisteresis, onChangePunto, tabTipo = 'ibc' }) => {
  const magnitudKey = magnitud?.magnitud ?? magnitud?.nombre;
  const {
    cantidadPuntos : cantPuntos,
    cantMedicionesPorPunto: cantMed,
    histeresis:requiereHisteresis,
  } = magnitud;

  // Refs para navegación entre inputs
  const inputRefs = useRef({});

  // Función para obtener/crear ref
  const getInputRef = (equipoId, puntoIdx, medicionIdx) => {
    const key = `${equipoId}-${puntoIdx}-${medicionIdx}`;
    if (!inputRefs.current[key]) {
      inputRefs.current[key] = React.createRef();
    }
    return inputRefs.current[key];
  };

  // Función para navegar entre celdas
  const handleNavigate = useCallback((equipoId, puntoIdx, medicionIdx, direction) => {
    const equiposCount = lotesCalibracion.length;
    const equipoIndex = lotesCalibracion.findIndex(l => (l.equipoInOt?._id ?? l.equipoInOt) === equipoId);

    let newEquipoIdx = equipoIndex;
    let newPuntoIdx = puntoIdx;
    let newMedicionIdx = medicionIdx;

    // Determinar canal para el fallback DOM (columna Histéresis vs normal).
    const canal = requiereHisteresis && puntoIdx === cantPuntos ? 'histeresis' : 'normal';
    // Cuando el input es de histéresis, en el DOM su data-punto refleja `cantPuntos`
    // (columna dedicada). Los queries del hermano usan el mismo esquema.

    switch (direction) {
      case 'up':
        if (equipoIndex === 0) {
          // Fuera del top → intentar saltar a la tabla hermana (misma magnitud).
          if (focusSiblingCell({
            magKey: magnitudKey, currentTipo: tabTipo, direction: 'up',
            punto: puntoIdx, medicion: medicionIdx, canal
          })) return;
        }
        newEquipoIdx = Math.max(0, equipoIndex - 1);
        break;
      case 'down':
        if (equipoIndex === equiposCount - 1) {
          if (focusSiblingCell({
            magKey: magnitudKey, currentTipo: tabTipo, direction: 'down',
            punto: puntoIdx, medicion: medicionIdx, canal
          })) return;
        }
        newEquipoIdx = Math.min(equiposCount - 1, equipoIndex + 1);
        break;
      case 'left':
        if (medicionIdx > 0) {
          newMedicionIdx = medicionIdx - 1;
        } else if (puntoIdx > 0) {
          newPuntoIdx = puntoIdx - 1;
          newMedicionIdx = cantMed - 1;
        }
        break;
      case 'right':
        if (medicionIdx < cantMed - 1) {
          newMedicionIdx = medicionIdx + 1;
        } else if (puntoIdx < cantPuntos - 1 || (requiereHisteresis && puntoIdx === cantPuntos - 1)) {
          newPuntoIdx = puntoIdx < cantPuntos - 1 ? puntoIdx + 1 : puntoIdx;
          newMedicionIdx = 0;
        }
        break;
      default:
        return;
    }

    // Enfocar el nuevo input dentro de la misma tabla.
    const newEquipoId = lotesCalibracion[newEquipoIdx]?.equipoInOt?._id ?? lotesCalibracion[newEquipoIdx]?.equipoInOt;
    const newKey = `${newEquipoId}-${newPuntoIdx}-${newMedicionIdx}`;
    const newRef = inputRefs.current[newKey];

    if (newRef && newRef.current) {
      newRef.current.focus();
      newRef.current.select();
    }
  }, [lotesCalibracion, cantPuntos, cantMed, requiereHisteresis, magnitudKey, tabTipo]);

  // Determinar color de fondo por columna de punto
  const getPuntoBackgroundColor = (puntoIdx) => {
    return puntoIdx % 2 === 0 ? '#FFFFFF' : '#F8F9FA';
  };

  return (
    <div className="mediciones-table-container">
      <table className="mediciones-table">
        <thead>
          {/* Fila 1 — grupos de puntos */}
          <tr>
            <th className="mediciones-table__th mediciones-table__th--left" rowSpan={2}>Equipo</th>
            {Array.from({ length: cantPuntos }).map((_, p) => (
              <th 
                key={p} 
                className="mediciones-table__th mediciones-table__th--punto" 
                colSpan={cantMed}
                style={{ backgroundColor: getPuntoBackgroundColor(p) }}
              >
                {datosPuntosCalibracion?.[p] || `Punto ${p + 1}`}
              </th>
            ))}
            {requiereHisteresis && (
              <th 
                className="mediciones-table__th mediciones-table__th--histeresis" 
                colSpan={cantMed}
                style={{ backgroundColor: getPuntoBackgroundColor(cantPuntos) }}
              >
                {datoHisteresis || `Pto.`} Histéresis
              </th>
            )}
          </tr>
          {/* Fila 2 — Valor + M1..Mn */}
          <tr>
            {Array.from({ length: cantPuntos }).map((_, p) => (
              <React.Fragment key={p}>
                {Array.from({ length: cantMed }).map((_, m) => (
                  <th 
                    key={m} 
                    className="mediciones-table__th-sub"
                    style={{ backgroundColor: getPuntoBackgroundColor(p) }}
                  >
                    M{m + 1}
                  </th>
                ))}
              </React.Fragment>
            ))}
            {requiereHisteresis && (
              <>
                {Array.from({ length: cantMed }).map((_, m) => (
                  <th 
                    key={m} 
                    className="mediciones-table__th-sub mediciones-table__th-sub--histeresis"
                    style={{ backgroundColor: getPuntoBackgroundColor(cantPuntos) }}
                  >
                    H{m + 1}
                  </th>
                ))}
              </>
            )}
          </tr>
        </thead>
        <tbody>
          {lotesCalibracion.map((lote) => {
            const equipoId = lote.equipoInOt?._id ?? lote.equipoInOt;
            const datos = datosIBC[equipoId]?.[magnitudKey] || {};
            return (
              <tr key={equipoId}>
                <CeldaEquipo lote={lote} />
                {/* Puntos normales — cada (equipo, punto) chequea dispersión.
                    Si σ excede umbral (abs + relativo), la celda de cada medición
                    y el badge σ del punto se resaltan en rojo. Ayuda a detectar
                    errores de digitación tipo 149.3 / 149.2 / 194.2. */}
                {Array.from({ length: cantPuntos }).map((_, p) => {
                  const puntoCal   = datos.puntoCalibracion?.[p] ?? '';
                  const mediciones = datos.mediciones?.[p] || [];
                  const bgColor = getPuntoBackgroundColor(p);
                  const disp = _computeDispersion(mediciones);
                  const tdStyle = disp.exceeds
                    ? { ...OUTLIER_TD_STYLE }
                    : { backgroundColor: bgColor };
                  return (
                    <React.Fragment key={p}>
                      {Array.from({ length: cantMed }).map((_, m) => (
                        <td
                          key={m}
                          className={`mediciones-table__td mediciones-table__td--input${disp.exceeds ? ' mediciones-table__td--outlier' : ''}`}
                          style={tdStyle}
                          title={disp.std !== null ? `σ = ${_fmt(disp.std)} — promedio ${_fmt(disp.mean)}` : ''}
                        >
                          <MedicionInput
                            value={mediciones[m]}
                            onChange={(v) => onChangePunto(equipoId, lote._id, 'mediciones', v, m, p)}
                            placeholder={`M${m + 1}`}
                            inputRef={getInputRef(equipoId, p, m)}
                            onNavigate={(dir) => handleNavigate(equipoId, p, m, dir)}
                            dataAttrs={{
                              'data-mag': magnitudKey,
                              'data-tipo': tabTipo,
                              'data-punto': p,
                              'data-medicion': m,
                              'data-canal': 'normal',
                            }}
                          />
                          {/* σ badge — solo en la última celda de cada grupo para no repetir */}
                          {m === cantMed - 1 && disp.std !== null && (
                            <div style={disp.exceeds ? OUTLIER_BADGE_STYLE : OK_BADGE_STYLE}>
                              {disp.exceeds ? '⚠ ' : ''}σ={_fmt(disp.std)}
                            </div>
                          )}
                        </td>
                      ))}
                    </React.Fragment>
                  );
                })}

                {/* Columna Histéresis */}
                {requiereHisteresis && (() => {
                  const medsHist = datos.puntoHisteresis?.mediciones || [];
                  const dispHist = _computeDispersion(medsHist);
                  const bgHist = dispHist.exceeds
                    ? { ...OUTLIER_TD_STYLE }
                    : { backgroundColor: getPuntoBackgroundColor(cantPuntos) };
                  return (
                    <>
                      {Array.from({ length: cantMed }).map((_, m) => (
                        <td
                          key={m}
                          className={`mediciones-table__td mediciones-table__td--input mediciones-table__td--histeresis${dispHist.exceeds ? ' mediciones-table__td--outlier' : ''}`}
                          style={bgHist}
                          title={dispHist.std !== null ? `σ = ${_fmt(dispHist.std)} — promedio ${_fmt(dispHist.mean)}` : ''}
                        >
                          <MedicionInput
                            value={medsHist[m]}
                            onChange={(v) => onChangePunto(equipoId, lote._id, 'histeresisMedicion', v, m, null)}
                            placeholder={`H${m + 1}`}
                            inputRef={getInputRef(equipoId, cantPuntos, m)}
                            onNavigate={(dir) => handleNavigate(equipoId, cantPuntos, m, dir)}
                            dataAttrs={{
                              'data-mag': magnitudKey,
                              'data-tipo': tabTipo,
                              'data-punto': cantPuntos,
                              'data-medicion': m,
                              'data-canal': 'histeresis',
                            }}
                          />
                          {m === cantMed - 1 && dispHist.std !== null && (
                            <div style={dispHist.exceeds ? OUTLIER_BADGE_STYLE : OK_BADGE_STYLE}>
                              {dispHist.exceeds ? '⚠ ' : ''}σ={_fmt(dispHist.std)}
                            </div>
                          )}
                        </td>
                      ))}
                    </>
                  );
                })()}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// ═════════════════════════════════════════════════════════════════
// LAYOUT FILAS
// Una fila por punto, equipos separados en bloques
// Cuando: histeresisEnTodosLosPuntos=true  O  (cantPuntos>4 && cantMed>4)
// ═════════════════════════════════════════════════════════════════
const LayoutFilas = ({ magnitud, lotesCalibracion, datosIBC, datosPuntosCalibracion, datoHisteresis, onChangePunto, tabTipo = 'ibc' }) => {
  const magnitudKey = magnitud?.magnitud ?? magnitud?.nombre;
  const {
    cantidadPuntos: cantPuntos,
    cantMedicionesPorPunto: cantMed,
    histeresis : requiereHisteresis,
    histeresisEnTodosLosPuntos,
  } = magnitud;

  const showBadgeCol   = histeresisEnTodosLosPuntos;
  const showHisteresis = requiereHisteresis && !histeresisEnTodosLosPuntos;

  // Refs para navegación entre inputs
  const inputRefs = useRef({});

  // Función para obtener/crear ref
  const getInputRef = (equipoId, puntoIdx, medicionIdx, tipo = 'normal') => {
    const key = `${equipoId}-${puntoIdx}-${medicionIdx}-${tipo}`;
    if (!inputRefs.current[key]) {
      inputRefs.current[key] = React.createRef();
    }
    return inputRefs.current[key];
  };

  // Función para navegar entre celdas en layout de filas
  const handleNavigate = useCallback((equipoId, puntoIdx, medicionIdx, tipo, direction) => {
    const equipoIndex = lotesCalibracion.findIndex(l => (l.equipoInOt?._id ?? l.equipoInOt) === equipoId);
    const equiposCount = lotesCalibracion.length;
    
    let newEquipoIdx = equipoIndex;
    let newPuntoIdx = puntoIdx;
    let newMedicionIdx = medicionIdx;
    let newTipo = tipo;

    switch (direction) {
      case 'up':
        if (histeresisEnTodosLosPuntos && tipo === 'bajada') {
          newTipo = 'subida';
        } else if (puntoIdx > 0) {
          newPuntoIdx = puntoIdx - 1;
          if (histeresisEnTodosLosPuntos) {
            newTipo = 'bajada';
          }
        } else if (equipoIndex > 0) {
          newEquipoIdx = equipoIndex - 1;
          newPuntoIdx = cantPuntos - 1;
          if (histeresisEnTodosLosPuntos) {
            newTipo = 'bajada';
          }
        } else {
          // Top absoluto de esta tabla → intentar saltar a la tabla hermana.
          if (focusSiblingCell({
            magKey: magnitudKey, currentTipo: tabTipo, direction: 'up',
            punto: puntoIdx, medicion: medicionIdx, canal: tipo
          })) return;
        }
        break;

      case 'down':
        if (histeresisEnTodosLosPuntos && tipo === 'subida') {
          newTipo = 'bajada';
        } else if (puntoIdx < cantPuntos - 1) {
          newPuntoIdx = puntoIdx + 1;
          if (histeresisEnTodosLosPuntos) {
            newTipo = 'subida';
          }
        } else if (showHisteresis && puntoIdx === cantPuntos - 1 && !histeresisEnTodosLosPuntos) {
          newPuntoIdx = -1; // histéresis
          newTipo = 'histeresis';
        } else if (equipoIndex < equiposCount - 1) {
          newEquipoIdx = equipoIndex + 1;
          newPuntoIdx = 0;
          if (histeresisEnTodosLosPuntos) {
            newTipo = 'subida';
          }
        } else {
          // Bottom absoluto → intentar saltar a la tabla hermana.
          if (focusSiblingCell({
            magKey: magnitudKey, currentTipo: tabTipo, direction: 'down',
            punto: puntoIdx, medicion: medicionIdx, canal: tipo
          })) return;
        }
        break;
      
      case 'left':
        if (medicionIdx > 0) {
          newMedicionIdx = medicionIdx - 1;
        }
        break;
      
      case 'right':
        if (medicionIdx < cantMed - 1) {
          newMedicionIdx = medicionIdx + 1;
        }
        break;
      
      default:
        return;
    }

    // Enfocar el nuevo input
    const newEquipoId = lotesCalibracion[newEquipoIdx]?.equipoInOt?._id ?? lotesCalibracion[newEquipoIdx]?.equipoInOt;
    const newKey = `${newEquipoId}-${newPuntoIdx}-${newMedicionIdx}-${newTipo}`;
    const newRef = inputRefs.current[newKey];
    
    if (newRef && newRef.current) {
      newRef.current.focus();
      newRef.current.select();
    }
  }, [lotesCalibracion, cantPuntos, cantMed, histeresisEnTodosLosPuntos, showHisteresis, magnitudKey, tabTipo]);

  return (
    <div className="mediciones-table-container">
      <table className="mediciones-table">
        <thead>
          <tr>
            <th className="mediciones-table__th mediciones-table__th--left">Equipo / Puntos</th>
            <th className="mediciones-table__th mediciones-table__th--left mediciones-table__th--width-80">Punto</th>
            {Array.from({ length: cantMed }).map((_, i) => (
              <th key={i} className="mediciones-table__th">
                {histeresisEnTodosLosPuntos ? `Col ${i + 1}` : `M${i + 1}`}
              </th>
            ))}
            {showBadgeCol && <th className="mediciones-table__th mediciones-table__th--width-80" />}
          </tr>
        </thead>
        <tbody>
          {lotesCalibracion.map((lote) => {
            const equipoId = lote.equipoInOt?._id ?? lote.equipoInOt;
            const datos    = datosIBC[equipoId]?.[magnitudKey] || {};
            const equipo   = lote.equipoInOt;

            return (
              <React.Fragment key={equipoId}>
                {Array.from({ length: cantPuntos }).map((_, puntoIdx) => {
                  const puntoCal       = datos.puntoCalibracion?.[puntoIdx]       ?? '';
                  const puntoBajada    = datos.puntoBajadaCalibracion?.[puntoIdx] ?? '';
                  const mediciones     = datos.mediciones?.[puntoIdx]             || [];
                  const medicionesSubida = datos.medicionesSubida?.[puntoIdx]     || [];
                  const medicionesBajada = datos.medicionesBajada?.[puntoIdx]     || [];

                  if (histeresisEnTodosLosPuntos) {
                    // Dos filas: subida y bajada
                    return (
                      <React.Fragment key={puntoIdx}>
                        {/* Subida */}
                        <tr className="mediciones-table__row--subida">
                          {puntoIdx === 0 && (
                            <td
                              className="mediciones-table__td mediciones-table__td--equipo mediciones-table__td--no-border-bottom"
                              rowSpan={cantPuntos * 2}
                            >
                              <div className="mediciones-table__equipo-label">{equipo?.consecutivo || '—'}</div>
                              <div className="mediciones-table__equipo-sub">{equipo?.equipo?.item?.nombre}</div>
                              <div className="mediciones-table__equipo-sub">{equipo?.equipo?.serie ? `S/N: ${equipo.equipo.serie}` : ''}</div>
                            </td>
                          )}
                          <td className="mediciones-table__td mediciones-table__td--no-border-bottom">
                            <span className="mediciones-table__punto-label">{datosPuntosCalibracion?.[puntoIdx] || `Punto ${puntoIdx + 1}`}</span>
                          </td>
                          {(() => {
                            const dispSub = _computeDispersion(medicionesSubida);
                            return Array.from({ length: cantMed }).map((_, m) => (
                              <td
                                key={m}
                                className={`mediciones-table__td mediciones-table__td--subida mediciones-table__td--no-border-bottom${dispSub.exceeds ? ' mediciones-table__td--outlier' : ''}`}
                                style={dispSub.exceeds ? OUTLIER_TD_STYLE : undefined}
                                title={dispSub.std !== null ? `σ subida = ${_fmt(dispSub.std)} — promedio ${_fmt(dispSub.mean)}` : ''}
                              >
                                <MedicionInput
                                  value={medicionesSubida[m]}
                                  onChange={(v) => onChangePunto(equipoId, lote._id, 'subida', v, m, puntoIdx)}
                                  placeholder={`S${m + 1}`}
                                  inputRef={getInputRef(equipoId, puntoIdx, m, 'subida')}
                                  onNavigate={(dir) => handleNavigate(equipoId, puntoIdx, m, 'subida', dir)}
                                  dataAttrs={{
                                    'data-mag': magnitudKey,
                                    'data-tipo': tabTipo,
                                    'data-punto': puntoIdx,
                                    'data-medicion': m,
                                    'data-canal': 'subida',
                                  }}
                                />
                                {m === cantMed - 1 && dispSub.std !== null && (
                                  <div style={dispSub.exceeds ? OUTLIER_BADGE_STYLE : OK_BADGE_STYLE}>
                                    {dispSub.exceeds ? '⚠ ' : ''}σ={_fmt(dispSub.std)}
                                  </div>
                                )}
                              </td>
                            ));
                          })()}
                          <td className="mediciones-table__td mediciones-table__td--subida mediciones-table__td--no-border-bottom">
                            <span className="mediciones-table__badge mediciones-table__badge--subida">↑ Subida</span>
                          </td>
                        </tr>
                        {/* Bajada */}
                        <tr className="mediciones-table__row--bajada">
                          <td className="mediciones-table__td">
                            <span className="mediciones-table__punto-sublabel">Bajada</span>
                          </td>
                          {(() => {
                            const dispBaj = _computeDispersion(medicionesBajada);
                            return Array.from({ length: cantMed }).map((_, m) => (
                              <td
                                key={m}
                                className={`mediciones-table__td mediciones-table__td--bajada${dispBaj.exceeds ? ' mediciones-table__td--outlier' : ''}`}
                                style={dispBaj.exceeds ? OUTLIER_TD_STYLE : undefined}
                                title={dispBaj.std !== null ? `σ bajada = ${_fmt(dispBaj.std)} — promedio ${_fmt(dispBaj.mean)}` : ''}
                              >
                                <MedicionInput
                                  value={medicionesBajada[m]}
                                  onChange={(v) => onChangePunto(equipoId, lote._id, 'bajada', v, m, puntoIdx)}
                                  placeholder={`B${m + 1}`}
                                  inputRef={getInputRef(equipoId, puntoIdx, m, 'bajada')}
                                  onNavigate={(dir) => handleNavigate(equipoId, puntoIdx, m, 'bajada', dir)}
                                  dataAttrs={{
                                    'data-mag': magnitudKey,
                                    'data-tipo': tabTipo,
                                    'data-punto': puntoIdx,
                                    'data-medicion': m,
                                    'data-canal': 'bajada',
                                  }}
                                />
                                {m === cantMed - 1 && dispBaj.std !== null && (
                                  <div style={dispBaj.exceeds ? OUTLIER_BADGE_STYLE : OK_BADGE_STYLE}>
                                    {dispBaj.exceeds ? '⚠ ' : ''}σ={_fmt(dispBaj.std)}
                                  </div>
                                )}
                              </td>
                            ));
                          })()}
                          <td className="mediciones-table__td mediciones-table__td--bajada">
                            <span className="mediciones-table__badge mediciones-table__badge--bajada">↓ Bajada</span>
                          </td>
                        </tr>
                      </React.Fragment>
                    );
                  }

                  // Fila normal — chequeo de dispersión por (equipo, punto)
                  const dispRow = _computeDispersion(mediciones);
                  return (
                    <tr key={puntoIdx} className={dispRow.exceeds ? 'mediciones-table__row--outlier' : ''}>
                      {puntoIdx === 0 && (
                        <td
                          className="mediciones-table__td mediciones-table__td--equipo"
                          rowSpan={cantPuntos + (showHisteresis ? 1 : 0)}
                        >
                          <div className="mediciones-table__equipo-label">{equipo?.consecutivo || '—'}</div>
                          <div className="mediciones-table__equipo-sub">{equipo?.equipo?.item?.nombre}</div>
                          <div className="mediciones-table__equipo-sub">{equipo?.equipo?.serie ? `S/N: ${equipo.equipo.serie}` : ''}</div>
                        </td>
                      )}
                      <td className="mediciones-table__td" style={dispRow.exceeds ? OUTLIER_TD_STYLE : undefined}>
                        <span className="mediciones-table__punto-label">Punto {puntoIdx + 1}</span>
                        {dispRow.std !== null && (
                          <div style={dispRow.exceeds ? OUTLIER_BADGE_STYLE : OK_BADGE_STYLE}>
                            {dispRow.exceeds ? '⚠ ' : ''}σ={_fmt(dispRow.std)}
                          </div>
                        )}
                      </td>
                      {Array.from({ length: cantMed }).map((_, m) => (
                        <td
                          key={m}
                          className={`mediciones-table__td${dispRow.exceeds ? ' mediciones-table__td--outlier' : ''}`}
                          style={dispRow.exceeds ? OUTLIER_TD_STYLE : undefined}
                          title={dispRow.std !== null ? `σ = ${_fmt(dispRow.std)} — promedio ${_fmt(dispRow.mean)}` : ''}
                        >
                          <MedicionInput
                            value={mediciones[m]}
                            onChange={(v) => onChangePunto(equipoId, lote._id, 'mediciones', v, m, puntoIdx)}
                            placeholder={`M${m + 1}`}
                            inputRef={getInputRef(equipoId, puntoIdx, m, 'normal')}
                            onNavigate={(dir) => handleNavigate(equipoId, puntoIdx, m, 'normal', dir)}
                            dataAttrs={{
                              'data-mag': magnitudKey,
                              'data-tipo': tabTipo,
                              'data-punto': puntoIdx,
                              'data-medicion': m,
                              'data-canal': 'normal',
                            }}
                          />
                        </td>
                      ))}
                    </tr>
                  );
                })}

                {/* Fila Histéresis por equipo — chequeo dispersión propio */}
                {showHisteresis && (() => {
                  const medsHist = datos.puntoHisteresis?.mediciones || [];
                  const dispHist = _computeDispersion(medsHist);
                  return (
                  <tr className={`mediciones-table__row--histeresis${dispHist.exceeds ? ' mediciones-table__row--outlier' : ''}`}>
                    <td className="mediciones-table__td" style={dispHist.exceeds ? OUTLIER_TD_STYLE : undefined}>
                      <span className="mediciones-table__histeresis-label">Pto. Histéresis</span>
                      {dispHist.std !== null && (
                        <div style={dispHist.exceeds ? OUTLIER_BADGE_STYLE : OK_BADGE_STYLE}>
                          {dispHist.exceeds ? '⚠ ' : ''}σ={_fmt(dispHist.std)}
                        </div>
                      )}
                    </td>
                    {Array.from({ length: cantMed }).map((_, m) => (
                      <td
                        key={m}
                        className={`mediciones-table__td mediciones-table__td--histeresis-row${dispHist.exceeds ? ' mediciones-table__td--outlier' : ''}`}
                        style={dispHist.exceeds ? OUTLIER_TD_STYLE : undefined}
                        title={dispHist.std !== null ? `σ = ${_fmt(dispHist.std)} — promedio ${_fmt(dispHist.mean)}` : ''}
                      >
                        <MedicionInput
                          value={medsHist[m]}
                          onChange={(v) => onChangePunto(equipoId, lote._id, 'histeresisMedicion', v, m, null)}
                          placeholder={`H${m + 1}`}
                          inputRef={getInputRef(equipoId, -1, m, 'histeresis')}
                          onNavigate={(dir) => handleNavigate(equipoId, -1, m, 'histeresis', dir)}
                          dataAttrs={{
                            'data-mag': magnitudKey,
                            'data-tipo': tabTipo,
                            'data-punto': -1,
                            'data-medicion': m,
                            'data-canal': 'histeresis',
                          }}
                        />
                      </td>
                    ))}
                  </tr>
                  );
                })()}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// ═════════════════════════════════════════════════════════════════
// COMPONENTE PRINCIPAL
//
// PUNTOS CONDICIONALES POR EQUIPO
// (spec lotes-estado-en-proceso-mediciones-condicionales):
// Si un equipo no tiene mediciones para un punto específico (ej. la plantilla
// agrega un punto nuevo `50 °C` que no aplica a este equipo), simplemente
// deja los inputs de ese punto vacíos. El backend aplica un filtro sparse
// (`esMedicionVacia`) que omite ese punto de `medicionesIBC[]` para ese equipo.
// Guard NO-WIPE ISO 17025 previene que un payload TODO vacío borre mediciones
// existentes — en ese caso el backend responde 422 `WIPE_BLOQUEADO`.
// ═════════════════════════════════════════════════════════════════
const PuntosMedicionTable = ({ magnitud, lotesCalibracion, datosIBC, datosPuntosCalibracion, datoHisteresis, onChangePunto, tabTipo = 'ibc' }) => {
  const { cantidadPuntos, cantMedicionesPorPunto, histeresisEnTodosLosPuntos } = magnitud;

  // Usar layout de filas cuando:
  //   1. Histéresis en todos los puntos (subida/bajada complica la transpuesta)
  //   2. Demasiadas columnas (cantPuntos > 4 Y cantMedicionesPorPunto > 4)
  const usarFilas =
    histeresisEnTodosLosPuntos ||
    (cantidadPuntos > 4 && cantMedicionesPorPunto > 4);

  return usarFilas
    ? <LayoutFilas       magnitud={magnitud} lotesCalibracion={lotesCalibracion} datosIBC={datosIBC} datosPuntosCalibracion={datosPuntosCalibracion} datoHisteresis={datoHisteresis} onChangePunto={onChangePunto} tabTipo={tabTipo} />
    : <LayoutTranspuesta magnitud={magnitud} lotesCalibracion={lotesCalibracion} datosIBC={datosIBC} datosPuntosCalibracion={datosPuntosCalibracion} datoHisteresis={datoHisteresis} onChangePunto={onChangePunto} tabTipo={tabTipo} />;
};

export default PuntosMedicionTable;